import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  LessonVideo,
  LessonInfo,
  LessonPager,
  LessonProgressBar,
  LessonSteps,
  LessonTabs,
} from '@/widgets/Lesson';
import { ContentWrapper } from '@/widgets/ContentWrapper';
import { CustomBreadcrumbs } from '@/widgets/CustomBreadcrumbs';
import { ScormCard, ScormModal } from '@/features/Scorm';
import { useCreateEnrollmentLessonNoteMutation } from '@/entities/Enrollment';
import { useGetScormQuery, useLazyGetScormLaunchQuery } from '@/entities/Scorm';
import type { IStudentScormPackage } from '@/entities/Scorm';
import { useRefreshOnVideoUrlExpiry } from '@/shared/lib/hooks';
import { getCourseDebtError } from '@/shared/lib/utils/getCourseDebtError';
import type { IStudentContentStep } from '@/shared/types/lesson';
import { Section } from '@/shared/ui/Section';
import { useEnrollmentLessonDetails } from '../../utils/hooks/useEnrollmentLessonDetails';

export const EnrollmentLessonView = () => {
  const { enrollmentId, intakeTermId, intakeModuleId, groupNumber } = useParams();

  const {
    data,
    isLoading,
    error,
    errorMessage,
    isParamsValid,
    breadcrumbs,
    params,
    steps,
    refetchLesson,
  } = useEnrollmentLessonDetails();

  const isStepsMode = steps.length > 0;
  const contentMode = data?.lessonData.lesson.contentMode;

  const [activeScorm, setActiveScorm] = useState<IStudentScormPackage | null>(null);
  const [launchingStepId, setLaunchingStepId] = useState<number | null>(null);

  const [createEnrollmentLessonNote, { isLoading: isSubmitting }] =
    useCreateEnrollmentLessonNoteMutation();

  const scormQueryParams = useMemo(
    () => ({
      groupLessonId: params.groupLessonId ? Number(params.groupLessonId) : undefined,
      groupExamResubmissionId: params.groupExamResubmissionId
        ? Number(params.groupExamResubmissionId)
        : undefined,
    }),
    [params.groupLessonId, params.groupExamResubmissionId],
  );
  const scormSkip =
    !data ||
    contentMode === 'steps' ||
    (!params.groupLessonId && !params.groupExamResubmissionId);
  const {
    data: scormData,
    error: scormError,
    refetch: refetchScorm,
  } = useGetScormQuery(scormQueryParams, {
    skip: scormSkip,
  });
  const [triggerLaunch, { isLoading: isLaunching, error: launchError }] =
    useLazyGetScormLaunchQuery();
  const [scormModalOpen, setScormModalOpen] = useState(false);
  const [scormUrl, setScormUrl] = useState<string | null>(null);

  const handleScormStart = useCallback(async () => {
    if (!scormData?.scorm) return;
    try {
      const result = await triggerLaunch({
        packageId: scormData.scorm.packageId,
        groupLessonId: scormQueryParams.groupLessonId,
        groupExamResubmissionId: scormQueryParams.groupExamResubmissionId,
      }).unwrap();
      setActiveScorm(scormData.scorm);
      setScormUrl(result?.launchUrl ?? null);
      setScormModalOpen(true);
    } catch {
      // ошибка launch — обработка через RTK Query
    }
  }, [scormData, triggerLaunch, scormQueryParams]);

  const handleStepScormStart = useCallback(
    async (step: IStudentContentStep) => {
      if (!step.scorm) return;
      setLaunchingStepId(step.id);
      try {
        const result = await triggerLaunch({
          packageId: step.scorm.packageId,
          groupLessonId: scormQueryParams.groupLessonId,
          groupExamResubmissionId: scormQueryParams.groupExamResubmissionId,
        }).unwrap();
        setActiveScorm(step.scorm);
        setScormUrl(result?.launchUrl ?? null);
        setScormModalOpen(true);
      } catch {
        // ошибка launch — обработка через RTK Query
      } finally {
        setLaunchingStepId(null);
      }
    },
    [triggerLaunch, scormQueryParams],
  );

  const handleScormClose = useCallback(() => {
    setScormModalOpen(false);
    setScormUrl(null);
    setActiveScorm(null);
    if (isStepsMode) refetchLesson();
    else refetchScorm();
  }, [isStepsMode, refetchLesson, refetchScorm]);

  const buildLessonLink = useCallback(
    (lesson: {
      order: number;
      groupLessonId?: number | null;
      groupExamResubmissionId?: number | null;
    }) => {
      const basePath = `/enrollments/${enrollmentId}/terms/${intakeTermId}/modules/${intakeModuleId}/groups/${groupNumber}`;
      return lesson.groupLessonId
        ? `${basePath}/groupLesson/${lesson.groupLessonId}`
        : `${basePath}/groupExamResubmission/${lesson.groupExamResubmissionId}`;
    },
    [enrollmentId, intakeTermId, intakeModuleId, groupNumber],
  );

  const handleCreateNote = useCallback(
    async ({ text, files }: { text: string; files: File[] }) => {
      await createEnrollmentLessonNote({
        groupLessonId: params.groupLessonId!,
        message: text,
        attachments: files.length > 0 ? files : undefined,
      }).unwrap();
    },
    [createEnrollmentLessonNote, params.groupLessonId],
  );

  const lessonData = data?.lessonData;
  const moduleData = data?.moduleData;
  const lesson = lessonData?.lesson;
  const course = moduleData?.course;

  const video = lesson?.video;
  const hasVideo = Boolean(video?.embedUrl);
  useRefreshOnVideoUrlExpiry(
    [video?.urlExpiresAt, ...steps.map((step) => step.video?.urlExpiresAt)],
    refetchLesson,
  );
  const assignmentHref = !lesson?.assignmentId
    ? ''
    : lesson?.groupLessonId
      ? `/assignments/groupLesson/${lesson.groupLessonId}`
      : `/assignments/groupExamResubmission/${lesson?.groupExamResubmissionId}`;

  const finalErrorMessage = useMemo(() => {
    if (!isParamsValid) return 'Invalid URL: missing required route params.';
    return errorMessage;
  }, [isParamsValid, errorMessage]);

  const debtError = useMemo(
    () => [error, scormError, launchError].find((candidate) => getCourseDebtError(candidate)),
    [error, scormError, launchError],
  );

  const isEmpty = !isLoading && !data;

  const emptyContent = (
    <Section className="space-y-4">
      <CustomBreadcrumbs items={breadcrumbs} />
      <div className="space-y-2 text-center">
        <h2 className="text-lg font-semibold">Lesson details are unavailable</h2>
        <p className="text-sm text-muted-foreground">
          The lesson may have been removed, or you may not have access. Use the breadcrumbs to go
          back, or return to the lessons list.
        </p>
      </div>
    </Section>
  );

  return (
    <ContentWrapper
      isLoading={isLoading}
      isEmpty={isEmpty}
      emptyContent={emptyContent}
      isError={!isParamsValid || !!errorMessage || !!debtError}
      error={debtError ?? error}
      errorMessage={finalErrorMessage}
      className="flex flex-col gap-y-6"
    >
      <CustomBreadcrumbs items={breadcrumbs} />
      {lesson && moduleData && course && (
        <>
          <LessonProgressBar
            lessons={moduleData.lessons}
            currentLessonOrder={lesson.order}
            buildLessonLink={buildLessonLink}
          />
          <LessonInfo
            module={course.title}
            numberLesson={lesson.order}
            title={lesson.title}
            endDate={lesson.endDate}
            scheduledAt={lesson.scheduledAt}
            moduleTeacher={lessonData?.moduleTeacher ?? null}
            lecturer={lessonData?.lecturer ?? null}
          />

          {isStepsMode ? (
            <LessonSteps
              key={params.groupLessonId ?? `r${params.groupExamResubmissionId}`}
              steps={steps}
              scormMode="launch"
              onScormAction={handleStepScormStart}
              scormLoadingStepId={launchingStepId}
            />
          ) : (
            <>
              {hasVideo && video && (
                <div className="w-full">
                  <LessonVideo video={video} />
                </div>
              )}
              {scormData?.scorm && (
                <ScormCard
                  scorm={scormData.scorm}
                  mode="launch"
                  onAction={handleScormStart}
                  isLoading={isLaunching}
                />
              )}
            </>
          )}

          <LessonTabs
            assignmentHref={assignmentHref}
            draftKey={`enrollments:lessons:${lesson.id}`}
            isSubmitting={isSubmitting}
            onCreateNote={handleCreateNote}
            lessonNotes={lessonData?.lessonNotes || []}
            lesson={lesson}
          />

          {activeScorm && (
            <ScormModal
              isOpen={scormModalOpen}
              onClose={handleScormClose}
              scorm={activeScorm}
              scormUrl={scormUrl}
            />
          )}

          <LessonPager
            lessons={moduleData.lessons}
            currentLessonOrder={lesson.order}
            buildLessonLink={buildLessonLink}
          />
        </>
      )}
    </ContentWrapper>
  );
};
